import { cs } from '../../lib/currency';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import api from '../../services/api';
import { toast } from 'sonner';
import PastUniformOrdersList from '../../components/parent/PastUniformOrdersList';
import {
  Shirt,
  User,
  CheckCircle,
  AlertCircle,
  Plus,
  Minus,
  ShoppingBag,
  Ruler,
  Info,
  Loader2,
  History,
  Clock,
  Package,
  XCircle,
} from 'lucide-react';

// Helper function to format price display
const formatPriceRange = (item) => {
  if (item.price_min === item.price_max) {
    return `${cs()}${item.price_min}`;
  }
  return `${cs()}${item.price_min}-${item.price_max}`;
};

const UniformVerification = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [campaignData, setCampaignData] = useState(null);
  const [activeTab, setActiveTab] = useState('children');
  
  // Player verification forms
  const [playerForms, setPlayerForms] = useState({});
  const [playerOrders, setPlayerOrders] = useState({});
  
  // Parent self-order
  const [_orderForSelf, setOrderForSelf] = useState(false);
  const [parentSizing, setParentSizing] = useState({
    shirt_name: '',
    shirt_number: '',
    chest_size: '',
    shirt_length: '',
    waist_size: '',
    trouser_length: '',
    sleeves: 'short'
  });
  const [parentItems, setParentItems] = useState([]);
  
  const [notes, setNotes] = useState('');
  
  // Order history
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchCampaignData();
    fetchOrderHistory();
  }, []);

  const fetchCampaignData = async () => {
    try {
      const response = await api.get('/uniform/active-campaign');
      setCampaignData(response.data);
      
      if (response.data.has_active_campaign && response.data.players) {
        // Initialize forms for each player
        const forms = {};
        const orders = {};
        
        response.data.players.forEach(player => {
          forms[player.id] = {
            shirt_name: player.shirt_name || '',
            shirt_number: player.shirt_number || '',
            chest_size: player.chest_size || '',
            shirt_length: player.shirt_length || '',
            waist_size: player.waist_size || '',
            trouser_length: player.trouser_length || '',
            sleeves: player.sleeves || 'short',
            verified: response.data.verified_player_ids?.includes(player.id) || false
          };
          orders[player.id] = [];
        });
        
        setPlayerForms(forms);
        setPlayerOrders(orders);
      }
    } catch (error) {
      toast.error('Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get('/uniform/orders');
      setOrderHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch order history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const updatePlayerForm = (playerId, field, value) => {
    setPlayerForms(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: value
      }
    }));
  };

  const addItemToPlayer = (playerId, itemType) => {
    setPlayerOrders(prev => ({
      ...prev,
      [playerId]: [
        ...prev[playerId],
        { item_type: itemType, quantity: 1, sleeves: playerForms[playerId]?.sleeves || 'short' }
      ]
    }));
  };

  const updatePlayerItem = (playerId, index, field, value) => {
    setPlayerOrders(prev => ({
      ...prev,
      [playerId]: prev[playerId].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removePlayerItem = (playerId, index) => {
    setPlayerOrders(prev => ({
      ...prev,
      [playerId]: prev[playerId].filter((_, i) => i !== index)
    }));
  };

  const addParentItem = (itemType) => {
    setParentItems(prev => [
      ...prev,
      { item_type: itemType, quantity: 1, sleeves: parentSizing.sleeves || 'short' }
    ]);
  };

  const updateParentItem = (index, field, value) => {
    setParentItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const removeParentItem = (index) => {
    setParentItems(prev => prev.filter((_, i) => i !== index));
  };

  const submitPlayerOrder = async (player) => {
    const playerId = player.id;
    const form = playerForms[playerId];
    const items = playerOrders[playerId];
    const isAlreadyVerified = form.verified;
    
    // If not already verified, validate required fields
    if (!isAlreadyVerified && !form.shirt_name?.trim()) {
      toast.error(`Please enter shirt name for ${player.full_name}`);
      return;
    }
    
    // If already verified and ordering items, check that items exist
    if (isAlreadyVerified && items.length === 0) {
      toast.error('Please add at least one item to order');
      return;
    }
    
    setSubmitting(true);
    try {
      // Determine what fields were updated (only for initial verification)
      const updatedFields = {};
      if (!isAlreadyVerified) {
        const originalPlayer = campaignData.players.find(p => p.id === playerId);
        
        ['shirt_name', 'shirt_number', 'chest_size', 'shirt_length', 'waist_size', 'trouser_length', 'sleeves'].forEach(field => {
          if (form[field] !== (originalPlayer[field] || '')) {
            updatedFields[field] = form[field];
          }
        });
      }
      
      await api.post('/uniform/orders', {
        campaign_id: campaignData.campaign.id,
        player_id: playerId,
        is_for_parent: false,
        verified_sizing: true,
        updated_fields: Object.keys(updatedFields).length > 0 ? updatedFields : null,
        items: items,
        notes: notes
      });
      
      const message = isAlreadyVerified 
        ? `Order submitted for ${player.full_name}` 
        : `Uniform verified for ${player.full_name}`;
      toast.success(message);
      
      // Update local state and clear items
      setPlayerForms(prev => ({
        ...prev,
        [playerId]: { ...prev[playerId], verified: true }
      }));
      setPlayerOrders(prev => ({
        ...prev,
        [playerId]: []
      }));
      
      // Refresh data and order history
      fetchCampaignData();
      fetchOrderHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const submitParentOrder = async () => {
    if (parentItems.length === 0) {
      toast.error('Please add at least one item to order');
      return;
    }
    
    // Get campaign items
    const items = campaignData?.campaign?.items || [];
    
    // Determine required fields based on items being ordered
    const requiresShirtSizing = parentItems.some(item => {
      const config = items.find(i => i.id === item.item_type);
      return config?.requires_shirt_sizing;
    });
    const requiresPantsSizing = parentItems.some(item => {
      const config = items.find(i => i.id === item.item_type);
      return config?.requires_pants_sizing;
    });
    
    // Name on shirt is required if ordering items that require shirt sizing
    if (requiresShirtSizing && !parentSizing.shirt_name?.trim()) {
      toast.error('Please enter a name for the shirt');
      return;
    }
    
    // Chest size is required for items that require shirt sizing
    if (requiresShirtSizing && !parentSizing.chest_size) {
      toast.error('Please enter chest size for shirt sizing');
      return;
    }
    
    // Waist size is required for items that require pants sizing
    if (requiresPantsSizing && !parentSizing.waist_size) {
      toast.error('Please enter waist size for pants sizing');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/uniform/orders', {
        campaign_id: campaignData.campaign.id,
        player_id: null,
        is_for_parent: true,
        verified_sizing: true,
        parent_sizing: parentSizing,
        items: parentItems,
        notes: notes
      });
      
      toast.success('Your uniform order has been submitted');
      setOrderForSelf(false);
      setParentItems([]);
      fetchCampaignData();
      fetchOrderHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Uniform Verification">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!campaignData?.has_active_campaign) {
    return (
      <DashboardLayout title="Uniform Verification">
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6 text-center py-10">
              <Shirt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No Active Uniform Campaign</h3>
              <p className="text-slate-500 mt-2">
                There is currently no uniform verification campaign active.
                Your past orders are listed below for reference.
              </p>
              <Button className="mt-4" variant="outline" onClick={() => navigate('/parent')}>
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
          {/* iter307ag — Surface past uniform orders so parents can
              still see what they previously ordered when a campaign is
              closed. */}
          <PastUniformOrdersList heading="Your past uniform orders" />
        </div>
      </DashboardLayout>
    );
  }

  const { campaign, players, pending_players, all_verified } = campaignData;

  // Get campaign items or use empty array
  const campaignItems = campaign.items || [];

  // Check if campaign deadline has passed
  const isDeadlinePassed = campaign?.deadline 
    ? new Date(campaign.deadline + 'T23:59:59') < new Date() 
    : false;

  // Helper to find item by id
  const getItemById = (itemId) => campaignItems.find(i => i.id === itemId);

  // Check if any item requires sizing
  const _hasShirtSizingItem = (itemId) => {
    const item = getItemById(itemId);
    return item?.requires_shirt_sizing || false;
  };

  const _hasPantsSizingItem = (itemId) => {
    const item = getItemById(itemId);
    return item?.requires_pants_sizing || false;
  };

  const hasSleeveOption = (itemId) => {
    const item = getItemById(itemId);
    return item?.has_sleeve_option || false;
  };

  return (
    <DashboardLayout title="Uniform Verification & Order">
      <div className="space-y-6" data-testid="uniform-verification-page">
        {/* Campaign Info */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Shirt className="h-6 w-6" />
                  {campaign.title}
                </CardTitle>
                {campaign.description && (
                  <CardDescription className="mt-2 text-blue-700">
                    {campaign.description}
                  </CardDescription>
                )}
              </div>
              {campaign.deadline && (
                (() => {
                  const isPastDeadline = new Date(campaign.deadline + 'T23:59:59') < new Date();
                  return isPastDeadline 
                    ? <Badge className="bg-red-100 text-red-700">Orders Closed ({new Date(campaign.deadline).toLocaleDateString()})</Badge>
                    : <Badge className="bg-amber-100 text-amber-800">Deadline: {new Date(campaign.deadline).toLocaleDateString()}</Badge>;
                })()
              )}
            </div>
          </CardHeader>
          <CardContent>
            {campaignItems.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Info className="h-4 w-4 text-blue-600" />
                <span className="text-blue-800 font-medium">Available Items:</span>
                {campaignItems.map((item, idx) => (
                  <Badge key={idx} variant="outline" className="bg-white">
                    {item.label}: {formatPriceRange(item)}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <AlertCircle className="h-4 w-4" />
                <span>No items configured for this campaign. Contact admin.</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deadline Passed Banner */}
        {isDeadlinePassed && (
          <Card className="border-2 border-red-300 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
                <div>
                  <p className="text-red-800 font-semibold">Order Deadline Has Passed</p>
                  <p className="text-red-600 text-sm">
                    The deadline for this campaign was {new Date(campaign.deadline).toLocaleDateString()}. 
                    New orders are no longer accepted. Please contact administration for any questions.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        {players.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {all_verified ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-amber-500" />
                  )}
                  <div>
                    <p className="font-medium">
                      {all_verified 
                        ? 'All children verified!' 
                        : `${pending_players.length} of ${players.length} children need verification`
                      }
                    </p>
                    <p className="text-sm text-slate-500">
                      {all_verified 
                        ? 'You can still order additional items below'
                        : 'Please verify uniform details for each child'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="children" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              My Children ({players.length})
            </TabsTrigger>
            <TabsTrigger value="self" className="flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" />
              Order for Myself
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Order History ({orderHistory.length})
            </TabsTrigger>
          </TabsList>

          {/* Children Tab */}
          <TabsContent value="children" className="space-y-4">
            {players.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center py-8">
                  <p className="text-slate-500">No children found in your account.</p>
                </CardContent>
              </Card>
            ) : (
              <Accordion type="multiple" defaultValue={pending_players.map(p => p.id)} className="space-y-4">
                {players.map(player => {
                  const form = playerForms[player.id] || {};
                  const items = playerOrders[player.id] || [];
                  const isVerified = form.verified;
                  
                  return (
                    <AccordionItem key={player.id} value={player.id} className="border rounded-lg overflow-hidden">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline bg-slate-50">
                        <div className="flex items-center gap-3 w-full">
                          <div className={`p-2 rounded-full ${isVerified ? 'bg-green-100' : 'bg-amber-100'}`}>
                            {isVerified ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <User className="h-5 w-5 text-amber-600" />
                            )}
                          </div>
                          <div className="text-left flex-1">
                            <p className="font-medium">{player.full_name}</p>
                            <p className="text-sm text-slate-500">
                              {isVerified ? 'Verified' : 'Needs verification'}
                            </p>
                          </div>
                          {isVerified && (
                            <Badge className="bg-green-100 text-green-700 mr-4">Verified</Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-6 pt-4">
                          {/* Sizing Form */}
                          <div>
                            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                              <Ruler className="h-4 w-4" />
                              Uniform Measurements
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <Label htmlFor={`${player.id}-shirt-name`}>Name on Shirt *</Label>
                                <Input
                                  id={`${player.id}-shirt-name`}
                                  value={form.shirt_name || ''}
                                  onChange={(e) => updatePlayerForm(player.id, 'shirt_name', e.target.value)}
                                  placeholder="e.g., SMITH"
                                  disabled={isVerified}
                                />
                              </div>
                              <div>
                                <Label htmlFor={`${player.id}-shirt-number`}>Shirt Number</Label>
                                <Input
                                  id={`${player.id}-shirt-number`}
                                  value={form.shirt_number || ''}
                                  onChange={(e) => updatePlayerForm(player.id, 'shirt_number', e.target.value)}
                                  placeholder="e.g., 7"
                                  disabled={isVerified}
                                />
                              </div>
                              <div>
                                <Label htmlFor={`${player.id}-chest`}>Chest Size (inches)</Label>
                                <Input
                                  id={`${player.id}-chest`}
                                  type="number"
                                  value={form.chest_size || ''}
                                  onChange={(e) => updatePlayerForm(player.id, 'chest_size', e.target.value)}
                                  placeholder="e.g., 32"
                                  disabled={isVerified}
                                />
                              </div>
                              <div>
                                <Label htmlFor={`${player.id}-shirt-length`}>Shirt Length (inches)</Label>
                                <Input
                                  id={`${player.id}-shirt-length`}
                                  type="number"
                                  value={form.shirt_length || ''}
                                  onChange={(e) => updatePlayerForm(player.id, 'shirt_length', e.target.value)}
                                  placeholder="e.g., 26"
                                  disabled={isVerified}
                                />
                              </div>
                              <div>
                                <Label htmlFor={`${player.id}-waist`}>Waist Size (inches)</Label>
                                <Input
                                  id={`${player.id}-waist`}
                                  type="number"
                                  value={form.waist_size || ''}
                                  onChange={(e) => updatePlayerForm(player.id, 'waist_size', e.target.value)}
                                  placeholder="e.g., 28"
                                  disabled={isVerified}
                                />
                              </div>
                              <div>
                                <Label htmlFor={`${player.id}-trouser`}>Trouser Length (inches)</Label>
                                <Input
                                  id={`${player.id}-trouser`}
                                  type="number"
                                  value={form.trouser_length || ''}
                                  onChange={(e) => updatePlayerForm(player.id, 'trouser_length', e.target.value)}
                                  placeholder="e.g., 38"
                                  disabled={isVerified}
                                />
                              </div>
                              <div className="col-span-2">
                                <Label>Sleeve Preference</Label>
                                <Select
                                  value={form.sleeves || 'short'}
                                  onValueChange={(v) => updatePlayerForm(player.id, 'sleeves', v)}
                                  disabled={isVerified}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="short">Short Sleeves</SelectItem>
                                    <SelectItem value="full">Full Sleeves</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>

                          {/* Order Items */}
                          <div>
                            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                              <ShoppingBag className="h-4 w-4" />
                              Order Additional Items (Optional)
                            </h4>
                            
                            {items.length > 0 && (
                              <div className="space-y-2 mb-4">
                                {items.map((item, index) => {
                                  const itemConfig = getItemById(item.item_type);
                                  return (
                                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                      <div className="flex-1">
                                        <p className="font-medium">
                                          {itemConfig?.label || item.item_type}
                                        </p>
                                        <p className="text-sm text-slate-500">
                                          {itemConfig ? formatPriceRange(itemConfig) : 'N/A'}
                                        </p>
                                      </div>
                                      {hasSleeveOption(item.item_type) && (
                                        <Select
                                          value={item.sleeves}
                                          onValueChange={(v) => updatePlayerItem(player.id, index, 'sleeves', v)}
                                        >
                                          <SelectTrigger className="w-32">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="short">Short</SelectItem>
                                            <SelectItem value="full">Full</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      )}
                                      <div className="flex items-center gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => updatePlayerItem(player.id, index, 'quantity', Math.max(1, item.quantity - 1))}
                                        >
                                          <Minus className="h-4 w-4" />
                                        </Button>
                                        <span className="w-8 text-center">{item.quantity}</span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => updatePlayerItem(player.id, index, 'quantity', item.quantity + 1)}
                                        >
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removePlayerItem(player.id, index)}
                                        className="text-red-500"
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {!isDeadlinePassed && (
                            <div className="flex flex-wrap gap-2">
                              {campaignItems.map(type => (
                                <Button
                                  key={type.id}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addItemToPlayer(player.id, type.id)}
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  {type.label.split(' ')[0]}
                                </Button>
                              ))}
                            </div>
                            )}
                          </div>

                          {/* Submit Button - show for unverified OR when items are added */}
                          {(!isVerified || items.length > 0) && !isDeadlinePassed && (
                            <div className="flex justify-end pt-4 border-t">
                              <Button
                                onClick={() => submitPlayerOrder(player)}
                                disabled={submitting}
                                className={isVerified ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"}
                              >
                                {submitting ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Submitting...
                                  </>
                                ) : isVerified ? (
                                  <>
                                    <ShoppingBag className="h-4 w-4 mr-2" />
                                    Submit Order ({items.length} item{items.length !== 1 ? 's' : ''})
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Verify & Submit
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </TabsContent>

          {/* Order for Self Tab */}
          <TabsContent value="self">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Order Uniform for Yourself
                </CardTitle>
                <CardDescription>
                  As a parent/supporter, you can order academy merchandise for yourself.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Info about required fields */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
                  <Info className="h-4 w-4 inline mr-2" />
                  <strong>Sizing requirements:</strong> Only fill in measurements for items you're ordering.
                  Each item has its own sizing requirements configured by the admin.
                </div>

                {/* Parent Sizing Form */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Ruler className="h-4 w-4" />
                    Your Measurements (fill based on items selected)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Name on Shirt {parentItems.some(i => {
                        const config = campaignItems.find(c => c.id === i.item_type);
                        return config?.requires_shirt_sizing;
                      }) && <span className="text-red-500">*</span>}</Label>
                      <Input
                        value={parentSizing.shirt_name}
                        onChange={(e) => setParentSizing(prev => ({ ...prev, shirt_name: e.target.value }))}
                        placeholder="e.g., SMITH"
                      />
                    </div>
                    <div>
                      <Label>Shirt Number</Label>
                      <Input
                        value={parentSizing.shirt_number}
                        onChange={(e) => setParentSizing(prev => ({ ...prev, shirt_number: e.target.value }))}
                        placeholder="e.g., 7"
                      />
                    </div>
                    <div>
                      <Label>Chest Size (inches) {parentItems.some(i => {
                        const config = campaignItems.find(c => c.id === i.item_type);
                        return config?.requires_shirt_sizing;
                      }) && <span className="text-red-500">*</span>}</Label>
                      <Input
                        type="number"
                        value={parentSizing.chest_size}
                        onChange={(e) => setParentSizing(prev => ({ ...prev, chest_size: e.target.value }))}
                        placeholder="e.g., 38"
                      />
                    </div>
                    <div>
                      <Label>Shirt Length (inches)</Label>
                      <Input
                        type="number"
                        value={parentSizing.shirt_length}
                        onChange={(e) => setParentSizing(prev => ({ ...prev, shirt_length: e.target.value }))}
                        placeholder="e.g., 28"
                      />
                    </div>
                    <div>
                      <Label>Waist Size (inches) {parentItems.some(i => {
                        const config = campaignItems.find(c => c.id === i.item_type);
                        return config?.requires_pants_sizing;
                      }) && <span className="text-red-500">*</span>}</Label>
                      <Input
                        type="number"
                        value={parentSizing.waist_size}
                        onChange={(e) => setParentSizing(prev => ({ ...prev, waist_size: e.target.value }))}
                        placeholder="e.g., 32"
                      />
                    </div>
                    <div>
                      <Label>Trouser Length (inches)</Label>
                      <Input
                        type="number"
                        value={parentSizing.trouser_length}
                        onChange={(e) => setParentSizing(prev => ({ ...prev, trouser_length: e.target.value }))}
                        placeholder="e.g., 40"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Sleeve Preference</Label>
                      <Select
                        value={parentSizing.sleeves}
                        onValueChange={(v) => setParentSizing(prev => ({ ...prev, sleeves: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="short">Short Sleeves</SelectItem>
                          <SelectItem value="full">Full Sleeves</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Parent Order Items */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4" />
                    Select Items to Order
                  </h4>
                  
                  {parentItems.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {parentItems.map((item, index) => {
                        const itemConfig = getItemById(item.item_type);
                        return (
                          <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium">
                                {itemConfig?.label || item.item_type}
                              </p>
                              <p className="text-sm text-slate-500">
                                {itemConfig ? formatPriceRange(itemConfig) : 'N/A'}
                              </p>
                            </div>
                            {hasSleeveOption(item.item_type) && (
                              <Select
                                value={item.sleeves}
                                onValueChange={(v) => updateParentItem(index, 'sleeves', v)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="short">Short</SelectItem>
                                  <SelectItem value="full">Full</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateParentItem(index, 'quantity', Math.max(1, item.quantity - 1))}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-8 text-center">{item.quantity}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateParentItem(index, 'quantity', item.quantity + 1)}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeParentItem(index)}
                              className="text-red-500"
                            >
                              Remove
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    {campaignItems.map(type => (
                      <Button
                        key={type.id}
                        variant="outline"
                        size="sm"
                        onClick={() => addParentItem(type.id)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {type.label.split(' ')[0]}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Additional Notes (Optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any special requests or notes..."
                    rows={3}
                  />
                </div>

                {/* Submit */}
                {isDeadlinePassed ? (
                  <div className="flex items-center gap-2 text-sm text-red-600 pt-4 border-t">
                    <AlertCircle className="h-4 w-4" />
                    <span>Order deadline has passed. New orders are no longer accepted.</span>
                  </div>
                ) : (
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={submitParentOrder}
                    disabled={submitting || parentItems.length === 0}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="submit-parent-order-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="h-4 w-4 mr-2" />
                        Submit Order {parentItems.length > 0 && `(${parentItems.length} item${parentItems.length > 1 ? 's' : ''})`}
                      </>
                    )}
                  </Button>
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Order History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Your Order History
                </CardTitle>
                <CardDescription>
                  View all your submitted uniform orders and their status.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : orderHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-700">No Orders Yet</h3>
                    <p className="text-slate-500 mt-2">Your submitted orders will appear here.</p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="order-history-list">
                    {orderHistory.map(order => {
                      const statusConfig = {
                        pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800', icon: Clock },
                        confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
                        processed: { label: 'Processed', color: 'bg-green-100 text-green-800', icon: Package },
                        cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
                      };
                      const status = statusConfig[order.status] || statusConfig.pending;
                      const StatusIcon = status.icon;
                      
                      return (
                        <div 
                          key={order.id} 
                          className="border rounded-lg p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                          data-testid={`order-${order.id}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${order.is_for_parent ? 'bg-purple-100' : 'bg-blue-100'}`}>
                                {order.is_for_parent ? (
                                  <User className="h-5 w-5 text-purple-600" />
                                ) : (
                                  <Shirt className="h-5 w-5 text-blue-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">
                                  {order.is_for_parent 
                                    ? 'Order for Yourself' 
                                    : `Order for ${order.player_data?.full_name || 'Player'}`
                                  }
                                </p>
                                <p className="text-sm text-slate-500">
                                  {order.campaign_title || 'Uniform Campaign'}
                                </p>
                              </div>
                            </div>
                            <Badge className={`${status.color} flex items-center gap-1`}>
                              <StatusIcon className="h-3 w-3" />
                              {status.label}
                            </Badge>
                          </div>
                          
                          {/* Order Items */}
                          {order.items && order.items.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-slate-600 mb-2">Items Ordered:</p>
                              <div className="flex flex-wrap gap-2">
                                {order.items.map((item, idx) => {
                                  const itemConfig = getItemById(item.item_type);
                                  return (
                                    <Badge key={idx} variant="outline" className="bg-white">
                                      {itemConfig?.label?.split(' ')[0] || item.item_type} 
                                      {item.quantity > 1 && ` × ${item.quantity}`}
                                      {item.sleeves && hasSleeveOption(item.item_type) && 
                                        ` (${item.sleeves === 'short' ? 'Short' : 'Full'} Sleeves)`
                                      }
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Sizing Info (for parent orders or verification) */}
                          {(order.is_for_parent && order.parent_sizing) || order.player_data ? (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-slate-600 mb-1">Sizing:</p>
                              <div className="text-sm text-slate-500 grid grid-cols-2 md:grid-cols-4 gap-2">
                                {order.is_for_parent && order.parent_sizing ? (
                                  <>
                                    {order.parent_sizing.shirt_name && (
                                      <span>Name: {order.parent_sizing.shirt_name}</span>
                                    )}
                                    {order.parent_sizing.shirt_number && (
                                      <span>Number: {order.parent_sizing.shirt_number}</span>
                                    )}
                                    {order.parent_sizing.chest_size && (
                                      <span>Chest: {order.parent_sizing.chest_size}"</span>
                                    )}
                                    {order.parent_sizing.waist_size && (
                                      <span>Waist: {order.parent_sizing.waist_size}"</span>
                                    )}
                                  </>
                                ) : order.player_data ? (
                                  <>
                                    {order.player_data.shirt_name && (
                                      <span>Name: {order.player_data.shirt_name}</span>
                                    )}
                                    {order.player_data.shirt_number && (
                                      <span>Number: {order.player_data.shirt_number}</span>
                                    )}
                                    {order.player_data.chest_size && (
                                      <span>Chest: {order.player_data.chest_size}"</span>
                                    )}
                                    {order.player_data.waist_size && (
                                      <span>Waist: {order.player_data.waist_size}"</span>
                                    )}
                                  </>
                                ) : null}
                              </div>
                            </div>
                          ) : null}

                          {/* Notes */}
                          {order.notes && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-slate-600">Notes:</p>
                              <p className="text-sm text-slate-500">{order.notes}</p>
                            </div>
                          )}
                          
                          {/* Order Date */}
                          <div className="flex items-center gap-1 text-xs text-slate-400 mt-2 pt-2 border-t">
                            <Clock className="h-3 w-3" />
                            Submitted: {new Date(order.created_at).toLocaleDateString()} at {new Date(order.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default UniformVerification;
